<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SendWindowFields from 'dashboard/components-next/follow-ups/SendWindowFields.vue';
import SendWindowBadge from 'dashboard/components-next/follow-ups/SendWindowBadge.vue';
import { isSendWindowValid } from 'dashboard/composables/useSendWindow';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const isLoading = ref(true);
const isSubmitting = ref(false);
const deletingId = ref(null);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const editingAutomation = ref(null);
const showDeleteModal = ref(false);
const automationToDelete = ref(null);

const delayValue = ref(0);
const delayUnit = ref('minutes');

// Inactivity threshold (conversation_inactivity trigger). Kept separate from the
// generic delay because it has different semantics: it's the customer-silence
// window measured from their last received message.
const inactivityValue = ref(1);
const inactivityUnit = ref('hours');

const form = ref({
  name: '',
  trigger_type: 'label_added',
  trigger_config: {},
  follow_up_template_id: null,
  delay_minutes: 0,
  enabled: true,
  send_window: {},
  content_mode: 'template',
  ai_instruction: '',
});

const isInactivityTrigger = computed(
  () => form.value.trigger_type === 'conversation_inactivity'
);
const isAiMode = computed(() => form.value.content_mode === 'ai');

const automations = computed(
  () => store.getters['followUpAutomations/getAutomations'] || []
);
const templates = computed(
  () => store.getters['followUpTemplates/getTemplates'] || []
);
const labels = computed(() => store.getters['labels/getLabels'] || []);

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('followUpAutomations/fetch'),
      store.dispatch('followUpTemplates/fetch'),
      store.dispatch('labels/get'),
    ]);
  } catch (error) {
    useAlert(t('FOLLOW_UP_AUTOMATIONS.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

const goBack = () => {
  router.push(accountScopedRoute('follow_ups'));
};

const getTriggerLabel = triggerType => {
  const triggerLabels = {
    label_added: t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.LABEL_ADDED'),
    label_removed: t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.LABEL_REMOVED'),
    contact_created: t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONTACT_CREATED'),
    conversation_created: t(
      'FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONVERSATION_CREATED'
    ),
    conversation_resolved: t(
      'FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONVERSATION_RESOLVED'
    ),
    conversation_inactivity: t(
      'FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONVERSATION_INACTIVITY'
    ),
  };
  return triggerLabels[triggerType] || triggerType;
};

const formatDelay = minutes => {
  if (!minutes || minutes === 0) {
    return t('FOLLOW_UP_AUTOMATIONS.DELAY_IMMEDIATE');
  }
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    return t('FOLLOW_UP_AUTOMATIONS.DELAY_DAYS', { count: days });
  }
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return t('FOLLOW_UP_AUTOMATIONS.DELAY_HOURS', { count: hours });
  }
  return t('FOLLOW_UP_AUTOMATIONS.DELAY_MINUTES', { count: minutes });
};

const resetForm = () => {
  form.value = {
    name: '',
    trigger_type: 'label_added',
    trigger_config: {},
    follow_up_template_id: templates.value[0]?.id || null,
    delay_minutes: 0,
    enabled: true,
    send_window: {},
    content_mode: 'template',
    ai_instruction: '',
  };
  delayValue.value = 0;
  delayUnit.value = 'minutes';
  inactivityValue.value = 1;
  inactivityUnit.value = 'hours';
};

const splitMinutes = minutes => {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    return { value: Math.floor(minutes / 1440), unit: 'days' };
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    return { value: Math.floor(minutes / 60), unit: 'hours' };
  }
  return { value: minutes, unit: 'minutes' };
};

const toMinutes = (value, unit) => {
  switch (unit) {
    case 'hours':
      return value * 60;
    case 'days':
      return value * 1440;
    default:
      return value;
  }
};

const parseDelay = minutes => {
  const { value, unit } = splitMinutes(minutes);
  delayValue.value = value;
  delayUnit.value = unit;
};

const editAutomation = automation => {
  editingAutomation.value = automation;
  form.value = {
    name: automation.name,
    trigger_type: automation.trigger_type,
    trigger_config: { ...automation.trigger_config },
    follow_up_template_id: automation.follow_up_template_id,
    delay_minutes: automation.delay_minutes,
    enabled: automation.enabled,
    send_window: { ...(automation.send_window || {}) },
    content_mode: automation.content_mode || 'template',
    ai_instruction: automation.ai_instruction || '',
  };
  parseDelay(automation.delay_minutes);
  const inactivity = splitMinutes(
    Number(automation.trigger_config?.inactivity_minutes) || 60
  );
  inactivityValue.value = inactivity.value;
  inactivityUnit.value = inactivity.unit;
  showEditModal.value = true;
};

const calculateDelayMinutes = () =>
  toMinutes(delayValue.value, delayUnit.value);

const closeModals = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  editingAutomation.value = null;
  resetForm();
};

const submitForm = async () => {
  if (!isSendWindowValid(form.value.send_window)) {
    useAlert(t('FOLLOW_UP_SEND_WINDOW.INVALID'));
    return;
  }
  if (isAiMode.value && !form.value.ai_instruction.trim()) {
    useAlert(t('FOLLOW_UP_AUTOMATIONS.FORM.AI_INSTRUCTION'));
    return;
  }
  if (!isAiMode.value && !form.value.follow_up_template_id) {
    useAlert(t('FOLLOW_UP_AUTOMATIONS.FORM.TEMPLATE_ERROR'));
    return;
  }
  if (isInactivityTrigger.value && inactivityValue.value <= 0) {
    useAlert(t('FOLLOW_UP_AUTOMATIONS.FORM.INACTIVITY'));
    return;
  }
  isSubmitting.value = true;
  try {
    const triggerConfig = { ...form.value.trigger_config };
    if (isInactivityTrigger.value) {
      triggerConfig.inactivity_minutes = toMinutes(
        inactivityValue.value,
        inactivityUnit.value
      );
    }
    const data = {
      ...form.value,
      trigger_config: triggerConfig,
      // For the inactivity trigger the silence window IS the timing, so the
      // generic post-trigger delay is forced to 0.
      delay_minutes: isInactivityTrigger.value ? 0 : calculateDelayMinutes(),
      // AI mode has no template; template mode carries no instruction.
      follow_up_template_id: isAiMode.value
        ? null
        : form.value.follow_up_template_id,
      ai_instruction: isAiMode.value ? form.value.ai_instruction.trim() : '',
    };

    if (showEditModal.value) {
      await store.dispatch('followUpAutomations/update', {
        id: editingAutomation.value.id,
        ...data,
      });
      useAlert(t('FOLLOW_UP_AUTOMATIONS.MESSAGES.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('followUpAutomations/create', data);
      useAlert(t('FOLLOW_UP_AUTOMATIONS.MESSAGES.CREATE_SUCCESS'));
    }

    closeModals();
  } catch (error) {
    // BUG-01 (audit 2026-06-16): surface the backend validation error (e.g.
    // "Name has already been taken") instead of swallowing it behind a generic
    // message. The modal stays open so the user can correct and retry.
    const message =
      error?.response?.data?.errors?.join(', ') ||
      error?.response?.data?.error ||
      t('FOLLOW_UP_AUTOMATIONS.MESSAGES.ERROR');
    useAlert(message);
  } finally {
    isSubmitting.value = false;
  }
};

const deleteAutomation = async automation => {
  deletingId.value = automation.id;
  try {
    await store.dispatch('followUpAutomations/delete', automation.id);
    useAlert(t('FOLLOW_UP_AUTOMATIONS.MESSAGES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('FOLLOW_UP_AUTOMATIONS.MESSAGES.ERROR'));
  } finally {
    deletingId.value = null;
  }
};

const confirmDelete = automation => {
  automationToDelete.value = automation;
  showDeleteModal.value = true;
};

const handleDeleteConfirm = async () => {
  if (automationToDelete.value) {
    await deleteAutomation(automationToDelete.value);
  }
  showDeleteModal.value = false;
  automationToDelete.value = null;
};

const handleDeleteCancel = () => {
  showDeleteModal.value = false;
  automationToDelete.value = null;
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="border-b border-n-weak bg-n-background px-6 py-4 flex-shrink-0"
    >
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <button
            type="button"
            class="rounded-lg p-1.5 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="goBack"
          >
            <span class="i-lucide-chevron-left size-5" aria-hidden="true" />
          </button>
          <div
            class="flex size-10 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand flex-shrink-0"
          >
            <span class="i-lucide-zap size-5" aria-hidden="true" />
          </div>
          <div>
            <h1 class="text-lg font-semibold text-n-slate-12">
              {{ $t('FOLLOW_UP_AUTOMATIONS.TITLE') }}
            </h1>
            <p class="text-xs text-n-slate-10">
              {{ $t('FOLLOW_UP_AUTOMATIONS.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
            @click="showCreateModal = true"
          >
            <span class="i-lucide-plus size-4" aria-hidden="true" />
            {{ $t('FOLLOW_UP_AUTOMATIONS.CREATE') }}
          </button>
        </div>
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-4">
      <!-- Loading State -->
      <div
        v-if="isLoading"
        class="flex flex-col items-center justify-center min-h-[360px]"
      >
        <div
          class="w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin mb-3"
        />
        <p class="text-[11px] font-medium text-n-slate-11">
          {{ $t('FOLLOW_UP_AUTOMATIONS.LOADING') }}
        </p>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="automations.length === 0"
        class="flex flex-col items-center justify-center min-h-[360px] text-center px-4"
      >
        <div
          class="size-16 rounded-2xl bg-n-brand/10 flex items-center justify-center mb-4"
        >
          <span class="i-lucide-zap size-8 text-n-brand" aria-hidden="true" />
        </div>
        <h3 class="text-sm font-semibold text-n-slate-12 mb-1.5">
          {{ $t('FOLLOW_UP_AUTOMATIONS.EMPTY.TITLE') }}
        </h3>
        <p class="text-[11px] text-n-slate-11 max-w-md mb-4">
          {{ $t('FOLLOW_UP_AUTOMATIONS.EMPTY.DESCRIPTION') }}
        </p>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          @click="showCreateModal = true"
        >
          <span class="i-lucide-plus size-4" aria-hidden="true" />
          {{ $t('FOLLOW_UP_AUTOMATIONS.EMPTY.CREATE_FIRST') }}
        </button>
      </div>

      <!-- Automations Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <div
          v-for="automation in automations"
          :key="automation.id"
          class="rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 hover:outline-n-strong transition-all duration-150 overflow-hidden"
        >
          <!-- Card Header -->
          <div class="p-3">
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <h3 class="text-[11px] font-semibold text-n-slate-12 truncate">
                  {{ automation.name }}
                </h3>
                <p class="text-[10px] text-n-slate-10 mt-0.5">
                  {{ getTriggerLabel(automation.trigger_type) }}
                </p>
              </div>
              <span
                class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 border"
                :class="[
                  automation.enabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800'
                    : 'bg-n-alpha-1 text-n-slate-11 border-n-weak',
                ]"
              >
                <span
                  class="size-1.5 rounded-full"
                  :class="
                    automation.enabled ? 'bg-emerald-500' : 'bg-n-slate-9'
                  "
                />
                {{
                  automation.enabled
                    ? $t('FOLLOW_UP_AUTOMATIONS.ACTIVE')
                    : $t('FOLLOW_UP_AUTOMATIONS.INACTIVE')
                }}
              </span>
            </div>
          </div>

          <!-- Card Body -->
          <div class="p-3 space-y-1.5">
            <div>
              <p
                class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide mb-0.5"
              >
                {{ $t('FOLLOW_UP_AUTOMATIONS.TEMPLATE') }}
              </p>
              <p
                v-if="automation.content_mode === 'ai'"
                class="inline-flex items-center gap-1 text-[11px] font-medium text-n-brand"
              >
                <span class="i-lucide-sparkles size-3" aria-hidden="true" />
                {{ $t('FOLLOW_UP_AUTOMATIONS.AI_MODE_LABEL') }}
              </p>
              <p v-else class="text-[11px] text-n-slate-11">
                {{ automation.follow_up_template_name || '-' }}
              </p>
            </div>
            <div>
              <p
                class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide mb-0.5"
              >
                {{ $t('FOLLOW_UP_AUTOMATIONS.DELAY') }}
              </p>
              <p class="text-[11px] text-n-slate-11">
                {{ formatDelay(automation.delay_minutes) }}
              </p>
              <SendWindowBadge
                v-if="automation.send_window && automation.send_window.enabled"
                :window="automation.send_window"
                class="mt-1"
              />
            </div>
            <div>
              <p
                class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide mb-0.5"
              >
                {{ $t('FOLLOW_UP_AUTOMATIONS.EXECUTIONS') }}
              </p>
              <p class="text-[11px] text-n-slate-11">
                {{ automation.executions_count || 0 }}
              </p>
            </div>
          </div>

          <!-- Card Footer -->
          <div
            class="px-2.5 py-1.5 border-t border-n-weak bg-n-background flex gap-1.5"
          >
            <NextButton
              size="sm"
              variant="ghost"
              color="slate"
              icon="edit"
              @click="editAutomation(automation)"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.EDIT') }}
            </NextButton>
            <NextButton
              size="sm"
              variant="ghost"
              color="ruby"
              icon="delete"
              :is-loading="deletingId === automation.id"
              @click="confirmDelete(automation)"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.DELETE.BUTTON') }}
            </NextButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      v-if="showCreateModal || showEditModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="closeModals"
    >
      <div
        class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
        >
          <div class="flex items-center gap-3">
            <div
              class="flex size-8 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand flex-shrink-0"
            >
              <span
                :class="showEditModal ? 'i-lucide-edit-3' : 'i-lucide-zap'"
                class="size-4"
                aria-hidden="true"
              />
            </div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{
                showEditModal
                  ? $t('FOLLOW_UP_AUTOMATIONS.FORM.EDIT_TITLE')
                  : $t('FOLLOW_UP_AUTOMATIONS.FORM.CREATE_TITLE')
              }}
            </h3>
          </div>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-slate-12 p-1.5 rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="closeModals"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>

        <!-- Modal Body -->
        <form class="p-4 space-y-4" @submit.prevent="submitForm">
          <!-- Name -->
          <div>
            <label
              for="fua-name"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.NAME') }}
            </label>
            <input
              id="fua-name"
              v-model="form.name"
              name="name"
              type="text"
              :placeholder="$t('FOLLOW_UP_AUTOMATIONS.FORM.NAME_PLACEHOLDER')"
              class="w-full h-8 px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 placeholder:text-n-slate-10"
              required
            />
          </div>

          <!-- Trigger Type -->
          <div>
            <label
              for="fua-trigger"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.TRIGGER_TYPE') }}
            </label>
            <div class="relative">
              <select
                id="fua-trigger"
                v-model="form.trigger_type"
                name="trigger_type"
                class="!mb-0 w-full h-8 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 appearance-none bg-none cursor-pointer"
                required
              >
                <option value="label_added">
                  {{ $t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.LABEL_ADDED') }}
                </option>
                <option value="label_removed">
                  {{ $t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.LABEL_REMOVED') }}
                </option>
                <option value="contact_created">
                  {{ $t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONTACT_CREATED') }}
                </option>
                <option value="conversation_created">
                  {{
                    $t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONVERSATION_CREATED')
                  }}
                </option>
                <option value="conversation_resolved">
                  {{
                    $t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONVERSATION_RESOLVED')
                  }}
                </option>
                <option value="conversation_inactivity">
                  {{
                    $t('FOLLOW_UP_AUTOMATIONS.TRIGGERS.CONVERSATION_INACTIVITY')
                  }}
                </option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
              </span>
            </div>
          </div>

          <!-- Label Selection (for label triggers) -->
          <div
            v-if="
              form.trigger_type === 'label_added' ||
              form.trigger_type === 'label_removed'
            "
          >
            <label
              for="fua-label"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.SELECT_LABEL') }}
            </label>
            <div class="relative">
              <select
                id="fua-label"
                v-model="form.trigger_config.label_id"
                name="label_id"
                class="!mb-0 w-full h-8 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 appearance-none bg-none cursor-pointer"
                required
              >
                <option value="" disabled>
                  {{
                    $t('FOLLOW_UP_AUTOMATIONS.FORM.SELECT_LABEL_PLACEHOLDER')
                  }}
                </option>
                <option
                  v-for="label in labels"
                  :key="label.id"
                  :value="label.id"
                >
                  {{ label.title }}
                </option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
              </span>
            </div>
          </div>

          <!-- Inactivity time (conversation_inactivity trigger) -->
          <div v-if="isInactivityTrigger">
            <label
              for="fua-inactivity"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.INACTIVITY') }}
            </label>
            <div class="flex items-center gap-2">
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <input
                id="fua-inactivity"
                v-model.number="inactivityValue"
                name="inactivity"
                type="number"
                min="1"
                placeholder="1"
                class="w-20 h-8 px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 text-center"
              />
              <!-- eslint-enable vue/no-bare-strings-in-template -->
              <div class="relative flex-1">
                <select
                  v-model="inactivityUnit"
                  name="inactivity_unit"
                  :aria-label="$t('FOLLOW_UP_AUTOMATIONS.FORM.INACTIVITY')"
                  class="!mb-0 w-full h-8 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 appearance-none bg-none cursor-pointer"
                >
                  <option value="minutes">
                    {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.MINUTES') }}
                  </option>
                  <option value="hours">
                    {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.HOURS') }}
                  </option>
                  <option value="days">
                    {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.DAYS') }}
                  </option>
                </select>
                <span
                  class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
                </span>
              </div>
            </div>
            <p class="mt-1.5 text-[11px] text-n-slate-10">
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.INACTIVITY_HINT') }}
            </p>
          </div>

          <!-- Content Mode: Template vs AI -->
          <div>
            <label class="block text-[11px] font-medium text-n-slate-12 mb-1.5">
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.CONTENT_MODE') }}
            </label>
            <div class="flex gap-2">
              <button
                type="button"
                class="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border text-[11px] font-medium transition-colors"
                :class="
                  !isAiMode
                    ? 'border-n-brand bg-n-brand/10 text-n-brand'
                    : 'border-n-weak bg-n-background text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="form.content_mode = 'template'"
              >
                <span class="i-lucide-file-text size-3.5" aria-hidden="true" />
                {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.CONTENT_MODE_TEMPLATE') }}
              </button>
              <button
                type="button"
                class="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border text-[11px] font-medium transition-colors"
                :class="
                  isAiMode
                    ? 'border-n-brand bg-n-brand/10 text-n-brand'
                    : 'border-n-weak bg-n-background text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="form.content_mode = 'ai'"
              >
                <span class="i-lucide-sparkles size-3.5" aria-hidden="true" />
                {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.CONTENT_MODE_AI') }}
              </button>
            </div>
          </div>

          <!-- Template Selection (template mode) -->
          <div v-if="!isAiMode">
            <label
              for="fua-template"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.TEMPLATE') }}
            </label>
            <div class="relative">
              <select
                id="fua-template"
                v-model="form.follow_up_template_id"
                name="follow_up_template_id"
                class="!mb-0 w-full h-8 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 appearance-none bg-none cursor-pointer"
                :required="!isAiMode"
              >
                <option value="" disabled>
                  {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.TEMPLATE_PLACEHOLDER') }}
                </option>
                <option
                  v-for="template in templates"
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.name }}
                </option>
              </select>
              <span
                class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              >
                <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
              </span>
            </div>
            <p class="mt-1.5 text-[11px] text-n-slate-10">
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.TEMPLATE_HINT') }}
            </p>
          </div>

          <!-- AI Instruction (AI mode) -->
          <div v-else>
            <label
              for="fua-ai-instruction"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.AI_INSTRUCTION') }}
            </label>
            <textarea
              id="fua-ai-instruction"
              v-model="form.ai_instruction"
              name="ai_instruction"
              rows="3"
              :placeholder="
                $t('FOLLOW_UP_AUTOMATIONS.FORM.AI_INSTRUCTION_PLACEHOLDER')
              "
              class="!mb-0 w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 placeholder:text-n-slate-10 resize-y"
              :required="isAiMode"
            />
            <p class="mt-1.5 text-[11px] text-n-slate-10">
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.AI_INSTRUCTION_HINT') }}
            </p>
          </div>

          <!-- Delay (non-inactivity triggers) -->
          <div v-if="!isInactivityTrigger">
            <label
              for="fua-delay"
              class="block text-[11px] font-medium text-n-slate-12 mb-1.5"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.DELAY') }}
            </label>
            <div class="flex items-center gap-2">
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <input
                id="fua-delay"
                v-model.number="delayValue"
                name="delay"
                type="number"
                min="0"
                placeholder="0"
                class="w-20 h-8 px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 text-center"
              />
              <!-- eslint-enable vue/no-bare-strings-in-template -->
              <div class="relative flex-1">
                <select
                  v-model="delayUnit"
                  name="delay_unit"
                  :aria-label="$t('FOLLOW_UP_AUTOMATIONS.FORM.DELAY')"
                  class="!mb-0 w-full h-8 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-background text-[11px] text-n-slate-12 appearance-none bg-none cursor-pointer"
                >
                  <option value="minutes">
                    {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.MINUTES') }}
                  </option>
                  <option value="hours">
                    {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.HOURS') }}
                  </option>
                  <option value="days">
                    {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.DAYS') }}
                  </option>
                </select>
                <span
                  class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
                </span>
              </div>
            </div>
            <p class="mt-1.5 text-[11px] text-n-slate-10">
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.DELAY_HINT') }}
            </p>
          </div>

          <!-- Send Window -->
          <div class="pt-1">
            <SendWindowFields v-model="form.send_window" />
          </div>

          <!-- Enabled -->
          <div class="pt-1">
            <label class="flex items-center gap-2.5 cursor-pointer">
              <input
                v-model="form.enabled"
                name="enabled"
                type="checkbox"
                class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand focus:ring-offset-0"
              />
              <span class="text-[11px] font-medium text-n-slate-12">
                {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.ENABLED') }}
              </span>
            </label>
          </div>

          <!-- Modal Footer -->
          <div class="flex gap-2.5 pt-3 border-t border-n-weak mt-4">
            <button
              type="button"
              class="flex-1 py-2 text-sm font-medium rounded-lg bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeModals"
            >
              {{ $t('FOLLOW_UP_AUTOMATIONS.FORM.CANCEL') }}
            </button>
            <button
              type="submit"
              class="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              :disabled="isSubmitting"
            >
              <span
                v-if="isSubmitting"
                class="i-lucide-loader-2 size-4 animate-spin"
                aria-hidden="true"
              />
              {{
                showEditModal
                  ? $t('FOLLOW_UP_AUTOMATIONS.FORM.UPDATE')
                  : $t('FOLLOW_UP_AUTOMATIONS.FORM.SAVE')
              }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="handleDeleteCancel"
    >
      <div
        class="bg-n-background rounded-2xl border border-n-weak shadow-xl w-full max-w-sm mx-4 overflow-hidden"
      >
        <!-- Modal icon + title -->
        <div class="px-6 pt-6 pb-4">
          <div class="flex items-start gap-4">
            <div
              class="flex size-10 flex-shrink-0 items-center justify-center rounded-xl bg-ruby-50 dark:bg-ruby-950/30"
            >
              <span
                class="i-lucide-trash-2 size-5 text-ruby-600 dark:text-ruby-400"
                aria-hidden="true"
              />
            </div>
            <div>
              <h3 class="text-base font-semibold text-n-slate-12 leading-snug">
                {{ $t('FOLLOW_UP_AUTOMATIONS.CONFIRM_DELETE_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ $t('FOLLOW_UP_AUTOMATIONS.CONFIRM_DELETE_HINT') }}
              </p>
            </div>
          </div>
        </div>
        <!-- Buttons -->
        <div class="flex gap-2 px-6 pb-6">
          <button
            type="button"
            class="flex-1 py-2 text-sm font-medium rounded-lg bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="handleDeleteCancel"
          >
            {{ $t('FOLLOW_UP_AUTOMATIONS.CONFIRM_DELETE_BACK') }}
          </button>
          <button
            type="button"
            class="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg bg-ruby-600 text-white hover:bg-ruby-700 transition-colors disabled:opacity-50"
            :disabled="deletingId === automationToDelete?.id"
            @click="handleDeleteConfirm"
          >
            <span
              v-if="deletingId === automationToDelete?.id"
              class="i-lucide-loader-2 size-4 animate-spin"
              aria-hidden="true"
            />
            {{ $t('FOLLOW_UP_AUTOMATIONS.CONFIRM_DELETE_BUTTON') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none;
}

select::-ms-expand {
  display: none;
}
</style>
